import React from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-GB",
    { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

/**
 * Shared listing component used by Essays, Excerpts, and Short Stories pages.
 * Renders the public-article index filtered by `kind`.
 */
export default function PublicArticleList({
  kind = "essay",
  overline,
  title,
  lede,
  emptyTitle = "Nothing here yet.",
  emptyNote = "It will live here when it's ready.",
  dataTestid = "public-list",
  baseRoute = "/essays",
  heroImage = null,         // { src, alt, caption? }  — optional header illustration
}) {
  const [items, setItems] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    axios.get(`${API}/articles?kind=${encodeURIComponent(kind)}`)
      .then(r => setItems(r.data))
      .catch(() => setError("Couldn't load these right now."));
  }, [kind]);

  return (
    <div data-testid={dataTestid}>
      {heroImage ? (
        <div className="grid md:grid-cols-[1fr,minmax(260px,380px)] gap-10 items-center mb-14">
          <div>
            <div className="overline-warm mb-4">{overline}</div>
            <h1 className="display-lg mb-6">{title}</h1>
            {lede && <p className="lede max-w-2xl">{lede}</p>}
          </div>
          <figure className="essay-hero"
                  data-testid={`${dataTestid}-hero`}>
            <img src={heroImage.src} alt={heroImage.alt || ""}
                 className="essay-hero-img"/>
            {heroImage.caption && (
              <figcaption className="essay-hero-caption">
                {heroImage.caption}
              </figcaption>
            )}
          </figure>
        </div>
      ) : (
        <>
          <div className="overline-warm mb-4">{overline}</div>
          <h1 className="display-lg mb-6">{title}</h1>
          {lede && <p className="lede max-w-2xl mb-14">{lede}</p>}
        </>
      )}

      {error && <p className="prose-warm">{error}</p>}
      {items === null && !error && <p className="muted">Gathering the pages…</p>}

      {items && items.length === 0 && (
        <div className="empty-warm">
          <p>{emptyTitle}</p>
          <p className="muted mt-2">{emptyNote}</p>
        </div>
      )}

      {items && items.length > 0 && (
        <div className="essays-list space-y-10">
          {items.map(a => (
            <article key={a.slug} className="essay-row">
              <Link to={`${baseRoute}/${a.slug}`} className="essay-link"
                    data-testid={`article-card-${a.slug}`}>
                <div className="essay-meta">
                  <span>{formatDate(a.published_at || a.created_at)}</span>
                  {a.tags?.length > 0 && (
                    <span className="essay-tags">
                      {a.tags.slice(0, 3).map(t => (
                        <span key={t} className="essay-tag">{t}</span>
                      ))}
                    </span>
                  )}
                </div>
                <h2 className="essay-title">{a.title}</h2>
                {a.summary && <p className="essay-summary">{a.summary}</p>}
              </Link>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
